/**
 * SubscribeToCommunityMessagesUseCase
 *
 * Live-update use case for community-wall messages. A thin wrapper over
 * `CommunityService.subscribeToMessages` that fans each delivered change out to
 * a per-change-type handler (`onAdded` / `onModified` / `onRemoved`) and returns
 * the `Unsubscribe` handle the caller is responsible for invoking on cleanup.
 *
 * Flutter source: lib/presentation/screens/community/community_screen.dart:280-358
 *   (`subscribeToLiveMessages`).
 *   - Query: `collectionGroup('community_messages')
 *       .where("messageUsersInfo.owner", isEqualTo: widget.user.firebaseId)
 *       .where("createdTimestamp", isGreaterThan: timestampChatObservation)
 *       .snapshots()` (community_screen.dart:286-291) — handled by
 *     `CommunityService.subscribeToMessages` / `firestoreService`.
 *   - The live cursor (`timestampChatObservation`) is the most-recent message's
 *     `createdTimestamp`, or `DateTime.now().millisecondsSinceEpoch` when the
 *     wall is empty (community_screen.dart:281-284). That fallback is the
 *     caller's responsibility — this use case forwards whatever `sinceTimestamp`
 *     it is handed.
 *   - community_screen.dart:299-355 — for each `docChange`, the doc is parsed
 *     and routed by `DocumentChangeType.added` / `.modified` / `.removed`. The
 *     presentation-side concerns (insert at index 0, scroll, unread badge,
 *     app-lifecycle gating, the "already in list before saving to DB" replace)
 *     stay in the feature hook on web — this use case is intentionally a thin
 *     domain pipe: route per change type, nothing more.
 *
 * Community messages are NOT end-to-end encrypted (community_screen.dart has no
 * decrypt step), so — unlike `SubscribeToChatUpdatesUseCase` — there is no E2EE
 * decryption fan-out here.
 *
 * Pure-domain contract (conventions.md § Clean Architecture flow):
 *   - Calls `CommunityService.subscribeToMessages`; does NOT touch
 *     `firestoreService` directly and does NOT dispatch to Redux.
 */

import type { Unsubscribe } from 'firebase/firestore';
import { CommunityService } from '@/domain/services/community/communityService';
import type { CommunityMessageChangeHandlers } from '@/domain/services/community/communityService';

export class SubscribeToCommunityMessagesUseCase {
  private communityService: CommunityService;

  constructor(communityService: CommunityService = CommunityService.getInstance()) {
    this.communityService = communityService;
  }

  /**
   * Subscribe to live community-wall message changes for `owner` arriving after
   * `sinceTimestamp`. Each change is routed to the matching per-change-type
   * handler.
   *
   * @param owner - The creator's firebaseId whose wall is observed (the
   *   `messageUsersInfo.owner` equality filter — community_screen.dart:289).
   * @param sinceTimestamp - Lower-exclusive cursor; the snapshot does NOT
   *   re-deliver any doc with `createdTimestamp <= sinceTimestamp`
   *   (`isGreaterThan`, community_screen.dart:290). The caller supplies the
   *   most-recent message's `createdTimestamp`, or `Date.now()` when the wall is
   *   empty (community_screen.dart:281-284).
   * @param handlers - Per-change-type handlers.
   * @returns The Firestore `Unsubscribe` handle produced by the service. The
   *   caller (the feature hook) is responsible for invoking it on cleanup.
   */
  execute(
    owner: string,
    sinceTimestamp: number,
    handlers: CommunityMessageChangeHandlers,
  ): Unsubscribe {
    return this.communityService.subscribeToMessages(owner, sinceTimestamp, handlers);
  }
}

export const subscribeToCommunityMessagesUseCase = new SubscribeToCommunityMessagesUseCase();
